"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Calendar, MessageCircle, Shield } from "lucide-react"
import { DEMO_WHATSAPP_NUMBER } from "@/lib/demo-whatsapp"
import type { PortfolioData } from "@/types/portfolio"

interface ContactProps {
  data: PortfolioData
}

export function Contact({ data }: ContactProps) {
  const { profile, contact } = data
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    clinica: "",
    dentistas: "1",
    sistema: "",
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  // Etiquetas legibles para los valores de los <select>, así el correo
  // no muestra los value crudos (ej. "2-5", "papel") sino texto claro.
  const dentistasLabels: Record<string, string> = {
    "1": "1 (consultorio)",
    "2-5": "2–5",
    "6+": "6 o más",
    multisede: "Multi-sede",
  }

  const sistemaLabels: Record<string, string> = {
    papel: "Papel / Excel",
    otro: "Otro software",
    nuevo: "Clínica nueva (sin sistema aún)",
  }

  const buildWhatsAppLink = () => {
    const message = [
      "*Solicitud de demo — AsistDental*",
      "",
      "*Contacto*",
      `Nombre: ${formData.nombre}`,
      `Correo: ${formData.email}`,
      "",
      "*Clínica*",
      `Nombre: ${formData.clinica || "No especificado"}`,
      `Dentistas: ${dentistasLabels[formData.dentistas] || formData.dentistas}`,
      `Usa hoy: ${sistemaLabels[formData.sistema] || "No especificado"}`,
      "",
      `_Enviado desde el sitio web de ${profile.name}_`,
    ].join("\n")

    return `https://wa.me/${DEMO_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Pequeño delay solo para mantener la sensación de "enviando..."
    await new Promise((resolve) => setTimeout(resolve, 600))

    // Abre WhatsApp (app o web) con el mensaje ya escrito.
    // TODO: cuando tengas API, sustituir esta línea por tu llamada real
    // (fetch a tu endpoint, guardar en base de datos, etc.) y quitar el WhatsApp.
    window.location.href = buildWhatsAppLink()

    setIsSubmitting(false)
    setSubmitted(true)
  }

  return (
    <section id="contacto" className="py-16 sm:py-20 lg:py-28 bg-[#f0fafa]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-10 sm:gap-12 lg:gap-16 items-start">
          {/* Copy — estilo Dentalink "Un paso seguro" */}
          <div className="lg:sticky lg:top-28">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-foreground leading-tight mb-4 sm:mb-5 text-balance">
              Un paso seguro para tu clínica dental
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Conversa con nuestro equipo y descubre cómo AsistDental se adapta
              al flujo de trabajo de tu consultorio. Demo en vivo, sin compromiso.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                "Recorrido personalizado por la plataforma",
                "Plan de implementación en 24 horas",
                "Resolución de dudas técnicas y operativas",
                "Sin permanencia ni tarjeta de crédito",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-foreground">
                  <Shield className="h-4 w-4 text-[#00676E] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <Button
              asChild
              size="lg"
              className="w-full sm:w-auto text-white h-12 px-8"
              style={{ backgroundColor: "#F97316" }}
            >
              <a href={`mailto:${contact.email}?subject=Demo AsistDental`}>
                <Calendar className="mr-2 h-5 w-5" />
                Agendar por correo
              </a>
            </Button>
          </div>

          {/* Formulario compacto — progressive disclosure style */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6 sm:p-8">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold mb-2">¡Listo!</h3>
                <p className="text-muted-foreground text-sm">
                  Se abrió WhatsApp con tu solicitud lista para enviar. Si no
                  se abrió automáticamente, escríbenos directo al{" "}
                  <a
                    href={`https://wa.me/${DEMO_WHATSAPP_NUMBER}`}
                    className="underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    +52 238 273 0259
                  </a>
                  .
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold mb-1">Solicita tu demo gratuita</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Cuéntanos de tu clínica y te mostramos {profile.name} en acción.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre</Label>
                    <Input
                      id="nombre"
                      placeholder="Dr. García"
                      required
                      value={formData.nombre}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Correo profesional</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@clinica.com"
                      required
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="clinica">Nombre de la clínica</Label>
                      <Input
                        id="clinica"
                        placeholder="Clínica Dental..."
                        value={formData.clinica}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dentistas">¿Cuántos dentistas?</Label>
                      <select
                        id="dentistas"
                        className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                        value={formData.dentistas}
                        onChange={handleChange}
                      >
                        <option value="1">1 (consultorio)</option>
                        <option value="2-5">2–5</option>
                        <option value="6+">6 o más</option>
                        <option value="multisede">Multi-sede</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sistema">¿Qué usas hoy?</Label>
                    <select
                      id="sistema"
                      className="w-full h-10 px-3 rounded-md border bg-background text-sm"
                      value={formData.sistema}
                      onChange={handleChange}
                    >
                      <option value="">Selecciona</option>
                      <option value="papel">Papel / Excel</option>
                      <option value="otro">Otro software</option>
                      <option value="nuevo">Clínica nueva</option>
                    </select>
                  </div>

                  <Button
                    type="submit"
                    className="w-full text-white h-12 text-base"
                    disabled={isSubmitting}
                    style={{ backgroundColor: "#F97316" }}
                  >
                    {isSubmitting ? "Enviando..." : "Solicitar mi demo gratuita"}
                    <Calendar className="ml-2 h-5 w-5" />
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    Sin spam. Solo para agendar tu demo personalizada.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}